import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../hooks/useAuth.tsx';
import { http } from '../lib/http';

const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      const errorParam = searchParams.get('error');
      const successParam = searchParams.get('success');

      // Handle success message (for registration pending)
      if (successParam === 'registration_pending') {
        navigate('/?success=registration_pending');
        return;
      }

      // Handle error from callback
      if (errorParam) {
        let errorMessage = 'Authentication failed';
        
        switch (errorParam) {
          case 'no_code':
            errorMessage = 'No authorization code received from Roblox';
            break;
          case 'oauth_failed':
            errorMessage = 'Roblox authentication failed. Please try again.';
            break;
          case 'pending_approval':
            errorMessage = 'Your account is pending approval. Please contact an administrator.';
            break;
          case 'account_rejected':
            errorMessage = 'Your account has been rejected. Please contact an administrator.';
            break;
          case 'invalid_state':
            errorMessage = 'Invalid authentication state. Please try again.';
            break;
          case 'service_unavailable':
            errorMessage = 'Authentication service temporarily unavailable. Please try again.';
            break;
          default:
            errorMessage = `Authentication error: ${errorParam}`;
        }
        
        setError(errorMessage);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
        return;
      }

      // Check if authentication was successful (cookie-based)
      if (successParam === 'true') {
        try {
          // Verify authentication with server (token is in httpOnly cookie)
          const response = await http.post('/auth/verify-cookie');

          if (response.data.success) {
            // Store Roblox ID for future silent logins
            const user = response.data.data.user;
            if (user.robloxId) {
              localStorage.setItem('roblox_id', user.robloxId);
              console.log('[OAuth Callback] Stored Roblox ID for future silent logins');
            }

            // Force a page reload to refresh authentication state
            // This ensures the AuthProvider picks up the new authentication
            window.location.href = '/';
          } else {
            setError('Authentication verification failed');
            setTimeout(() => {
              navigate('/');
            }, 2000);
          }
        } catch (err: any) {
          console.error('[OAuth Callback] Cookie verification error:', err);
          setError('Failed to verify authentication');
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
        return;
      }

      // No success or error parameter - redirect to login
      navigate('/');
    };

    processCallback();
  }, [searchParams, navigate]);

  // If already authenticated, redirect to main app
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#101216',
        gap: 2,
        p: 3,
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {error}
          </Alert>
          <Typography sx={{ color: '#b4b9c0' }}>
            Redirecting to login...
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={60} sx={{ color: '#3c8cff' }} />
          <Typography variant="h6" sx={{ color: '#eceef1' }}>
            Completing Roblox authentication...
          </Typography>
          <Typography sx={{ color: '#b4b9c0' }}>
            Please wait while we verify your account.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;
